import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useBookingPage } from "../../BookingPageContext";
import { normalizeExternalUrl } from "../../../../utils/urlUtils";
import { trackBookingConfirmed } from "../../../../services/analyticsTrackingService";
import { formatCurrency } from "../../../../utils/currency";

const ZERO_DECIMAL_CCY = new Set(['BIF','CLP','DJF','GNF','JPY','KMF','KRW','MGA','PYG','RWF','UGX','VND','VUV','XAF','XOF','XPF']);

function formatPayAmount(amount: number, currency: string): string {
  const cur = (currency || 'USD').toUpperCase();
  const minDigits = ZERO_DECIMAL_CCY.has(cur) ? 0 : 2;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: cur,
      minimumFractionDigits: minDigits,
      maximumFractionDigits: minDigits,
    }).format(amount);
  } catch {
    return `${amount} ${cur}`;
  }
}

const PayLaterInvoicePanel: React.FC<{
  payment: { hostedUrl: string; amount: number; currency: string };
}> = ({ payment }) => {
  const { t } = useTranslation('payments');
  return (
    <div className="max-w-md mx-auto bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
      <div className="flex items-start gap-3">
        <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-yellow-900">
            {t('payLater.title', 'Payment required')}
          </h4>
          <p className="text-sm text-yellow-800 mt-1">
            {t('payLater.body', 'Please complete payment of {{amount}} to finalise your booking.', {
              amount: formatPayAmount(payment.amount, payment.currency),
            })}
          </p>
          <a
            href={payment.hostedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center mt-3 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-medium rounded-md transition-colors"
          >
            {t('payLater.payNow', 'Pay invoice')}
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
};

interface BookingConfirmationProps {
  mode?: 'booking' | 'reschedule' | 'cancel' | 'preview';
  onPreviewRedirect?: (url: string) => void;
}

const BookingConfirmation: React.FC<BookingConfirmationProps> = ({ mode, onPreviewRedirect }) => {
  const { bookingDetails, meetingType, selectedTimezone } = useBookingPage();
  const { t, i18n } = useTranslation();
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Booking funnel step 3. Fired here rather than at submit time so it also
  // covers the Pay Now path, where the booking is only confirmed after Stripe
  // Elements completes. `mode` is undefined on the normal booking route;
  // reschedule/cancel/preview must not count as new bookings.
  //
  // Declared BEFORE the redirect effect so it runs first when a meeting type
  // has a custom bookingRedirectUrl (the request itself uses `keepalive`).
  useEffect(() => {
    if (mode && mode !== 'booking') return;
    trackBookingConfirmed(meetingType?.id);
  }, [mode, meetingType?.id]);

  // Handle redirect to external URL with booking data
  useEffect(() => {
    if (bookingDetails && meetingType?.bookingRedirectUrl) {
      const { booking } = bookingDetails;

      // Build query parameters with booking information
      const params = new URLSearchParams();

      // Add booking details as query parameters
      if (booking.id) params.append('bookingId', String(booking.id));
      if (booking.guestName) params.append('guestName', booking.guestName);
      if (booking.guestEmail) params.append('guestEmail', booking.guestEmail);
      if (booking.guestPhone) params.append('guestPhone', booking.guestPhone);
      if (booking.startTime) params.append('startTime', new Date(booking.startTime).toISOString());
      if (booking.endTime) params.append('endTime', new Date(booking.endTime).toISOString());
      if (booking.timeZone) params.append('timeZone', booking.timeZone);
      if (meetingType?.title) params.append('meetingTitle', meetingType.title);
      if (booking.host?.name) params.append('hostName', booking.host.name);
      if (booking.host?.email) params.append('hostEmail', booking.host.email);
      if (booking.status) params.append('status', booking.status);

      // Add video conference URL if available
      const existingVideoUrl = booking.meeting?.joinUrl || booking.videoConference?.joinUrl;
      if (existingVideoUrl) params.append('videoUrl', existingVideoUrl);

      // Normalize the redirect URL (add https:// if missing protocol)
      const normalizedBaseUrl = normalizeExternalUrl(meetingType.bookingRedirectUrl);

      // Build complete redirect URL with booking parameters
      const redirectUrl = normalizedBaseUrl.includes('?')
        ? `${normalizedBaseUrl}&${params.toString()}`
        : `${normalizedBaseUrl}?${params.toString()}`;

      // Handle redirect based on mode
      if (mode === 'preview') {
        onPreviewRedirect?.(redirectUrl);
      } else {
        setIsRedirecting(true);
        window.location.href = redirectUrl;
      }
    }
  }, [bookingDetails, meetingType, mode, onPreviewRedirect]);

  // Show redirecting message if redirect is in progress
  if (isRedirecting) {
    return (
      <div className="py-6">
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
            style={{ backgroundColor: 'var(--brand-color-very-light, #D1E9F5)' }}
          >
            <svg
              className="w-8 h-8 animate-spin"
              style={{ color: 'var(--brand-color, #2E7AA0)' }}
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-medium text-gray-800 mb-2">
            {t('booking:confirmation.redirecting', 'Redirecting...')}
          </h2>
          <p className="text-gray-600">
            {t('booking:confirmation.redirectingMessage', 'You are being redirected to complete your booking experience.')}
          </p>
        </div>
      </div>
    );
  }

  if (!bookingDetails) {
    return (
      <div className="py-6">
        <div className="text-center">
          <h2 className="text-2xl font-medium text-gray-800 mb-2">
            {t('booking:confirmation.title', 'Booking Confirmed!')}
          </h2>
          <p className="text-gray-600">
            {t('booking:confirmation.emailNotification', 'Your booking details will be sent to your email.')}
          </p>
        </div>
      </div>
    );
  }

  const { booking } = bookingDetails;
  const startTime = new Date(booking.startTime);
  const endTime = new Date(booking.endTime);

  // Use the booking's timezone instead of the context timezone to prevent state loss
  const bookingTimezone = booking.timeZone || selectedTimezone;

  // Get current locale for date formatting
  const locale = i18n.language || 'en';
  const isJapanese = locale === 'ja';

  // Format the date and time for display using the current language
  const formattedDate = startTime.toLocaleDateString(locale, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: bookingTimezone
  });

  const formattedTime = startTime.toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "numeric",
    hour12: !isJapanese, // 24-hour for Japanese, 12-hour for English
    timeZone: bookingTimezone
  }) + " - " + endTime.toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "numeric",
    hour12: !isJapanese, // 24-hour for Japanese, 12-hour for English
    timeZone: bookingTimezone
  });

  // Render meeting link note for virtual meetings
  // Video link is created in the background and included in the calendar invite and confirmation email.
  // Showing the pending/loading state on the confirmation page added complexity without UX benefit.
  const renderMeetingLinkNote = () => {
    if (!meetingType?.isVirtual) return null;

    return (
      <div className="flex flex-col items-center">
        <div className="flex items-center justify-center mb-2">
          <svg
            className="h-5 w-5 text-gray-500 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          <p className="text-gray-500 text-sm">
            {t('booking:confirmation.meetingLinkInEmail', 'The meeting link will be included in your confirmation email and calendar invite.')}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="py-6">
      <div className="text-center mb-8">
        <div
          className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
          style={{ backgroundColor: 'var(--brand-color-very-light, #D1E9F5)' }}
        >
          <svg
            className="w-8 h-8"
            style={{ color: 'var(--brand-color, #2E7AA0)' }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-medium text-gray-800 mb-2">
          {t('booking:confirmation.title', 'Booking Confirmed!')}
        </h2>
        <p className="text-gray-600">
          {t('booking:confirmation.subtitle', 'You will receive a confirmation email with the details.')}
        </p>

        {/* What they booked — and, when it was a plan, that they are now on it.
            Someone who has just subscribed needs to see the plan's name here:
            it is the only place that confirms WHICH plan the payment started,
            and it arrives before the email does. */}
        {bookingDetails?.bookedOptionName && (
          <p className="mt-3 text-sm text-gray-700">
            {bookingDetails.bookedOptionUpgraded
              ? bookingDetails.bookedOptionUpgradeAmount != null
                ? t(
                    'booking:confirmation.planUpgraded',
                    'You are now on {{name}}. The difference of {{amount}} / month has been charged to your card.',
                    {
                      name: bookingDetails.bookedOptionName,
                      amount: formatCurrency(
                        Number(bookingDetails.bookedOptionUpgradeAmount),
                        bookingDetails.bookedOptionUpgradeCurrency || 'JPY'
                      ),
                    }
                  )
                : t('booking:confirmation.planChanged', 'You are now on {{name}}.', {
                    name: bookingDetails.bookedOptionName,
                  })
              : bookingDetails.bookedOptionIsPlan
                ? t('booking:confirmation.planStarted', 'Your plan is active: {{name}}', {
                    name: bookingDetails.bookedOptionName,
                  })
                : bookingDetails.bookedOptionName}
          </p>
        )}
      </div>

      {/* Phase 4 — Pay Later invoice link */}
      {bookingDetails?.booking?._payment?.mode === 'pay_later' && bookingDetails.booking._payment.hostedUrl && (
        <PayLaterInvoicePanel payment={bookingDetails.booking._payment} />
      )}

      <div className="max-w-md mx-auto border border-gray-300 rounded-lg py-8 px-6 mb-8">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-2">
            <svg
              className="h-5 w-5 text-gray-500 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-800">
              {meetingType?.title || t('booking:form.meetingTitle', 'Meeting')}
            </h3>
          </div>
          <p className="text-gray-600 text-sm">
            {t('booking:confirmation.with', 'with {{host}}', {
              host: bookingDetails.booking.host?.name ||
                bookingDetails.hostName ||
                t('booking:confirmation.hostDefault', 'Host')
            })}
          </p>
        </div>

        <div className="space-y-6">
          <div className="flex flex-col items-center">
            <div className="flex items-center justify-center mb-2">
              <svg
                className="h-5 w-5 text-gray-500 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-center">
                <p className="text-gray-800 font-medium">{formattedDate}</p>
              </div>
            </div>
            <p className="text-gray-600 text-sm">{formattedTime}</p>
            <p className="text-gray-500 text-xs">{bookingTimezone}</p>
          </div>

          {renderMeetingLinkNote()}
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
